import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "../components/ui/dialog";
import {
  Library, MapPin, Headphones, Settings,
  Loader2, Globe, Share2, BarChart3, ArrowLeft, CreditCard, User, Star,
  FolderPlus, Folder, FolderOpen, ChevronDown, ChevronRight, ChevronUp, ImageOff,
  GripVertical, Pencil, Trash2, X, Plus, Heart
} from "lucide-react";
import { userApi } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { BottomNav } from "../components/BottomNav";
import { DesktopSidebar } from "../components/DesktopSidebar";
import { captureError } from "../lib/sentry";
import { resolveMediaUrl } from "../lib/apiConfig";
import { selectPreferredGuides, sortGuides } from "../lib/libraryUtils";

const CATEGORY_COLORS = {
  landmark:    { hex: "#f59e0b", label: "Landmarks" },
  museum:      { hex: "#6366f1", label: "Museums & Galleries" },
  historical:  { hex: "#57534e", label: "Historical Sites" },
  natural:     { hex: "#10b981", label: "Parks & Gardens" },
  religious:   { hex: "#9333ea", label: "Churches & Temples" },
  library:     { hex: "#0891b2", label: "Libraries" },
  theater:     { hex: "#f43f5e", label: "Theatres" },
  stadium:     { hex: "#ea580c", label: "Stadiums & Arenas" },
  cultural:    { hex: "#3b82f6", label: "Cultural" },
};

const LibraryThumbnail = ({ imageUrl, name }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [imageUrl]);

  return (
    <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center bg-[#ecebe8] dark:bg-[#302f2d] border border-black/5 dark:border-white/5">
      {imageUrl && !failed ? (
        <img
          src={resolveMediaUrl(imageUrl)}
          alt={name}
          className="w-full h-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        <ImageOff className="w-5 h-5 text-[#85827d] dark:text-[#aaa7a1]" aria-label="No image available" />
      )}
    </div>
  );
};

/* ─── Draggable Guide Card (reused in root AND folders) ─── */
const GuideCard = ({ item, onDragStart, onDragEnd, navigate, isDragging, dragData }) => {
  const cardRef = useRef(null);
  const generating = item.has_audio === false;

  const handleClick = () => {
    navigate(`/player/${item.place_id}?lang=${item.language || "en"}`);
  };

  return (
    <div
      ref={cardRef}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("application/json", JSON.stringify(dragData));
        e.dataTransfer.effectAllowed = "move";
        onDragStart(item.place_id);
      }}
      onDragEnd={() => onDragEnd()}
      className={`transition-all ${isDragging ? "opacity-40 scale-95" : ""}`}
      data-testid={`guide-card-${item.place_id}`}
    >
      <Card
        className={`cursor-grab active:cursor-grabbing hover:border-primary/30 transition-colors ${generating ? "opacity-60" : ""}`}
        onClick={handleClick}
      >
      <CardContent className="flex items-center gap-3 py-3 px-4">
        <GripVertical className="w-4 h-4 text-muted-foreground/40 flex-shrink-0" />
        <LibraryThumbnail imageUrl={item.image_url} name={item.place_name} />
        <div className="min-w-0 flex-1">
          <p className="font-medium text-sm truncate">{item.place_name}</p>
          {generating ? (
            <p className="text-xs text-primary flex items-center gap-1" data-testid={`guide-generating-${item.place_id}`}>
              <Loader2 className="w-3 h-3 animate-spin" /> Generating audio guide…
            </p>
          ) : (
            <p className="text-xs text-muted-foreground truncate">{item.summary_preview}</p>
          )}
          <p className="text-[10px] text-muted-foreground/60 mt-0.5">
            {new Date(item.listened_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
            {item.language && ` · ${item.language.toUpperCase()}`}
          </p>
        </div>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          {generating ? (
            <Loader2 className="w-4 h-4 animate-spin text-primary" />
          ) : item.user_rating ? (
            [1, 2, 3, 4, 5].map(s => (
              <Star key={s} className={`w-3.5 h-3.5 ${s <= item.user_rating ? "fill-amber-400 text-amber-400" : "text-gray-200"}`} />
            ))
          ) : (
            <Headphones className="w-4 h-4 text-muted-foreground/40" />
          )}
        </div>
      </CardContent>
    </Card>
  </div>
  );
};

/* ─── Folder Component ─── */
const FolderCard = ({ folder, historyByPlace, navigate, onRename, onDelete, onGuideDrop, onFolderDrop, draggingPlaceId, draggingFolderId, setDraggingPlaceId, setDraggingFolderId, clearDragging, isDefault, index, sortMode, sortAscending }) => {
  const [expanded, setExpanded] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [folderDragOver, setFolderDragOver] = useState(false);
  const folderItems = sortGuides((folder.place_ids || [])
    .map(pid => historyByPlace[pid])
    .filter(Boolean), sortMode, sortAscending);

  const handleDragOver = (e) => {
    e.preventDefault();
    try {
      const types = Array.from(e.dataTransfer.types);
      if (types.includes("application/x-folder")) {
        e.dataTransfer.dropEffect = "move";
        setFolderDragOver(true);
      } else {
        e.dataTransfer.dropEffect = "move";
        setDragOver(true);
      }
    } catch {
      e.dataTransfer.dropEffect = "move";
      setDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOver(false);
      setFolderDragOver(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    setFolderDragOver(false);
    // Check if this is a folder reorder drop
    const folderData = e.dataTransfer.getData("application/x-folder");
    if (folderData) {
      try {
        const data = JSON.parse(folderData);
        if (data.folderId && data.folderId !== folder.id) {
          onFolderDrop(data.folderId, index);
        }
      } catch {}
      return;
    }
    // Otherwise it's a guide drop
    try {
      const data = JSON.parse(e.dataTransfer.getData("application/json"));
      if (data.placeId) {
        onGuideDrop(folder.id, data.placeId, data.fromFolderId || null);
      }
    } catch {}
  };

  return (
    <div
      className={`transition-all ${
        dragOver ? "bg-primary/5 rounded-lg" : ""
      } ${folderDragOver ? "border-t-2 border-primary" : ""}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      data-testid={`folder-${folder.id}`}
    >
      {/* Folder Header — draggable for reordering */}
      <div
        className={`flex items-center gap-1 ${draggingFolderId === folder.id ? "opacity-40" : ""}`}
      >
        <div
          draggable
          className="cursor-grab active:cursor-grabbing p-1 text-muted-foreground/40 hover:text-muted-foreground flex-shrink-0"
          onDragStart={(e) => {
            e.dataTransfer.setData("application/x-folder", JSON.stringify({ folderId: folder.id }));
            e.dataTransfer.effectAllowed = "move";
            setDraggingFolderId(folder.id);
          }}
          onDragEnd={() => setDraggingFolderId(null)}
          data-testid={`folder-grip-${folder.id}`}
        >
          <GripVertical className="w-4 h-4" />
        </div>
        <button
          className="flex-1 flex items-center gap-3 py-2.5 text-left min-w-0"
          onClick={() => setExpanded(!expanded)}
          data-testid={`folder-toggle-${folder.id}`}
        >
          {expanded ? (
            <FolderOpen className={`w-5 h-5 flex-shrink-0 ${isDefault ? "text-rose-500" : "text-primary"}`} />
          ) : (
            <Folder className={`w-5 h-5 flex-shrink-0 ${isDefault ? "text-rose-500" : "text-primary"}`} />
          )}
          <span className="flex-1 font-medium text-sm truncate">{folder.name}</span>
          <span className="text-xs text-muted-foreground tabular-nums">{folderItems.length}</span>
          {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
        </button>
        {expanded && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
              onClick={() => onRename(folder)}
              data-testid={`folder-rename-${folder.id}`}
            >
              <Pencil className="w-3 h-3" />
            </button>
            {!isDefault && (
              <button
                className="text-xs text-muted-foreground hover:text-destructive flex items-center gap-1 transition-colors"
                onClick={() => onDelete(folder)}
                data-testid={`folder-delete-${folder.id}`}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Folder Contents — items at full width, same as root */}
      {expanded && (
        <div className="space-y-2 mt-1">
          {folderItems.length === 0 ? (
            <div className="py-4 text-center border border-dashed border-border rounded-xl">
              <p className="text-xs text-muted-foreground">
                {draggingPlaceId ? "Drop a guide here" : "Drag audio guides here to organize"}
              </p>
            </div>
          ) : (
            folderItems.map(item => (
              <GuideCard
                key={item.place_id}
                item={item}
                onDragStart={setDraggingPlaceId}
                onDragEnd={clearDragging}
                navigate={navigate}
                isDragging={draggingPlaceId === item.place_id}
                dragData={{ placeId: item.place_id, fromFolderId: folder.id }}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};


const DashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tappedCountry, setTappedCountry] = useState(null);
  const [sortMode, setSortMode] = useState("newest");
  const [sortAscending, setSortAscending] = useState(false);

  // Folder state
  const [folders, setFolders] = useState([]);
  const [foldersLoading, setFoldersLoading] = useState(true);
  const [draggingPlaceId, setDraggingPlaceId] = useState(null);
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [renamingFolder, setRenamingFolder] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [deletingFolder, setDeletingFolder] = useState(null);
  const [rootDragOver, setRootDragOver] = useState(false);
  const [draggingFolderId, setDraggingFolderId] = useState(null);

  useEffect(() => {
    Promise.all([
      userApi.getHistory().then(r => setHistory(r.data || [])),
      userApi.getStats().then(r => setStats(r.data)).catch(() => setStats(null)),
      userApi.getFolders().then(r => setFolders(r.data.folders || [])).catch(() => setFolders([])),
    ]).finally(() => { setLoading(false); setFoldersLoading(false); });
  }, []);

  // While any guide is still generating audio, silently poll so the
  // spinner resolves into a playable guide without a manual refresh.
  const anyGenerating = history.some(h => h.has_audio === false);
  useEffect(() => {
    if (!anyGenerating) return;
    const t = setInterval(() => {
      userApi.getHistory().then(r => setHistory(r.data || [])).catch(() => {});
    }, 6000);
    return () => clearInterval(t);
  }, [anyGenerating]);

  // A place can have several language entitlements. Show the user's selected
  // audio language when available, then the newest owned language as fallback.
  const preferredGuides = selectPreferredGuides(history, user?.default_audio_language || "en");
  const historyByPlace = Object.fromEntries(preferredGuides.map((item) => [item.place_id, item]));

  // Compute which place_ids are in ANY folder
  const placesInFolders = new Set();
  for (const f of folders) {
    for (const pid of (f.place_ids || [])) {
      placesInFolders.add(pid);
    }
  }

  // Root list = only guides NOT in any folder
  const rootGuides = sortGuides(
    Object.values(historyByPlace).filter(item => !placesInFolders.has(item.place_id)),
    sortMode,
    sortAscending,
  );

  const selectSortMode = (mode) => {
    setSortMode(mode);
    setSortAscending(mode === "alpha");
  };

  const clearDragging = useCallback(() => setDraggingPlaceId(null), []);

  // Unified drop handler: moves a place into targetFolderId, removing from sourceFolderId if present
  const handleDrop = async (targetFolderId, placeId, sourceFolderId) => {
    // If already in this folder, do nothing
    const targetFolder = folders.find(f => f.id === targetFolderId);
    if (targetFolder?.place_ids?.includes(placeId)) return;

    // Remove from source folder first (if dragging from a folder)
    if (sourceFolderId) {
      try {
        await userApi.removePlaceFromFolder(sourceFolderId, placeId);
        setFolders(prev => prev.map(f =>
          f.id === sourceFolderId
            ? { ...f, place_ids: (f.place_ids || []).filter(id => id !== placeId) }
            : f
        ));
      } catch {
        captureError(new Error("Failed to remove guide from source folder"), {
          action: "move_guide_remove_source",
          sourceFolderId,
          targetFolderId,
          placeId,
        });
        toast.error("Failed to move guide");
        return;
      }
    }

    // Add to target folder
    try {
      await userApi.addPlaceToFolder(targetFolderId, placeId);
      setFolders(prev => prev.map(f =>
        f.id === targetFolderId
          ? { ...f, place_ids: [...(f.place_ids || []), placeId] }
          : f
      ));
      toast.success("Guide moved");
    } catch (error) {
      captureError(error, { action: "move_guide_to_folder", targetFolderId, placeId });
      toast.error("Failed to move guide");
    }
  };

  // Drop onto root = remove from whatever folder it's in
  const handleRootDrop = async (e) => {
    e.preventDefault();
    setRootDragOver(false);
    // Ignore folder reorder drags landing on root
    if (e.dataTransfer.getData("application/x-folder")) return;
    try {
      const data = JSON.parse(e.dataTransfer.getData("application/json"));
      if (data.placeId && data.fromFolderId) {
        await userApi.removePlaceFromFolder(data.fromFolderId, data.placeId);
        setFolders(prev => prev.map(f =>
          f.id === data.fromFolderId
            ? { ...f, place_ids: (f.place_ids || []).filter(id => id !== data.placeId) }
            : f
        ));
        toast.success("Guide moved back");
      }
    } catch (error) {
      captureError(error, { action: "move_guide_to_root" });
      toast.error("Failed to move guide");
    }
  };

  // Reorder folder: move draggedFolderId to targetIndex
  const handleFolderReorder = async (draggedFolderId, targetIndex) => {
    const sorted = [...folders].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    const fromIndex = sorted.findIndex(f => f.id === draggedFolderId);
    if (fromIndex === -1 || fromIndex === targetIndex) return;

    const reordered = [...sorted];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(targetIndex, 0, moved);

    // Optimistic update with new positions
    const updated = reordered.map((f, i) => ({ ...f, position: i }));
    setFolders(updated);

    try {
      await userApi.reorderFolders(updated.map(f => f.id));
    } catch (error) {
      captureError(error, { action: "reorder_folders" });
      toast.error("Failed to reorder folders");
      // Revert
      setFolders(sorted);
    }
  };

  // Folder CRUD
  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    try {
      const res = await userApi.createFolder(name);
      setFolders(prev => [...prev, res.data]);
      setNewFolderName("");
      setCreateFolderOpen(false);
      toast.success("Folder created");
    } catch (err) {
      if (!err.response || err.response.status >= 500) {
        captureError(err, { action: "create_folder" });
      }
      toast.error(err.response?.data?.detail || "Failed to create folder");
    }
  };

  const handleRenameFolder = async () => {
    if (!renamingFolder || !renameValue.trim()) return;
    try {
      await userApi.renameFolder(renamingFolder.id, renameValue.trim());
      setFolders(prev => prev.map(f => f.id === renamingFolder.id ? { ...f, name: renameValue.trim() } : f));
      setRenamingFolder(null);
      toast.success("Folder renamed");
    } catch (err) {
      if (!err.response || err.response.status >= 500) {
        captureError(err, { action: "rename_folder", folderId: renamingFolder.id });
      }
      toast.error(err.response?.data?.detail || "Failed to rename folder");
    }
  };

  const handleDeleteFolder = async () => {
    if (!deletingFolder) return;
    try {
      await userApi.deleteFolder(deletingFolder.id);
      setFolders(prev => prev.filter(f => f.id !== deletingFolder.id));
      setDeletingFolder(null);
      toast.success("Folder deleted");
    } catch (err) {
      if (!err.response || err.response.status >= 500) {
        captureError(err, { action: "delete_folder", folderId: deletingFolder.id });
      }
      toast.error(err.response?.data?.detail || "Failed to delete folder");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const totalGuides = stats?.total_guides || 0;
  const numCountries = stats?.num_countries || 0;
  const numShared = stats?.num_shared || 0;
  const breakdown = stats?.category_breakdown || {};
  const countryList = stats?.country_list || [];
  const maxBarVal = Math.max(totalGuides, numCountries, numShared, 1);

  const catEntries = Object.entries(breakdown).sort((a, b) => b[1] - a[1]);
  const stackSegments = catEntries.map(([cat, count]) => ({
    cat,
    pct: totalGuides > 0 ? (count / totalGuides) * 100 : 0,
    hex: CATEGORY_COLORS[cat]?.hex || "#94a3b8",
    label: CATEGORY_COLORS[cat]?.label || cat,
    count,
  }));

  const sortedFolders = [...folders].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

  return (
    <div className="min-h-screen bg-background pb-20 lg:pl-64">
      <DesktopSidebar active="/dashboard" />
      <header className="sticky top-0 bg-background/95 backdrop-blur border-b z-10">
        <div className="max-w-lg mx-auto flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => navigate("/explore")} className="rounded-xl" data-testid="dashboard-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <Library className="w-5 h-5 text-primary" />
            <h1 className="text-xl font-bold">Library</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="rounded-xl h-8 text-xs gap-1.5"
              onClick={() => navigate("/explore?myplaces=1")}
              data-testid="map-my-places-btn"
            >
              <MapPin className="w-3.5 h-3.5" />
              Map - my places
            </Button>
            <Button variant="ghost" size="icon" onClick={() => navigate("/settings")} className="rounded-xl" data-testid="dashboard-settings-btn">
              <Settings className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-5 space-y-6">
        {/* My Stats */}
        <section data-testid="my-stats-section">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 className="w-4 h-4 text-primary" />
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">My Stats</h2>
          </div>

          <Card>
            <CardContent className="pt-5 pb-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="flex flex-col items-center">
                  <span className="text-2xl font-bold mb-1">{totalGuides}</span>
                  <div className="w-12 h-28 rounded-lg bg-muted overflow-hidden flex flex-col-reverse">
                    {stackSegments.map((seg) => (
                      <div
                        key={seg.cat}
                        style={{
                          height: `${totalGuides > 0 ? (seg.count / maxBarVal) * 100 : 0}%`,
                          backgroundColor: seg.hex,
                          minHeight: seg.count > 0 ? "3px" : "0",
                        }}
                        title={`${seg.label}: ${seg.count}`}
                      />
                    ))}
                  </div>
                  <div className="flex items-center gap-1 mt-2">
                    <Headphones className="w-3.5 h-3.5 text-muted-foreground" />
                    <span className="text-[11px] text-muted-foreground text-center">Total guides</span>
                  </div>
                </div>

                <div className="flex flex-col items-center">
                  <span className="text-2xl font-bold mb-1">{numCountries}</span>
                  <div className="relative w-12 h-28">
                    <div className="absolute inset-0 rounded-lg bg-muted overflow-hidden flex flex-col-reverse">
                      {countryList.length > 0 ? countryList.map((country, i) => {
                        const color = `hsl(${210 + i * 30}, 70%, ${50 + i * 8}%)`;
                        return (
                          <div
                            key={country}
                            style={{
                              height: `${maxBarVal > 0 ? (1 / maxBarVal) * 100 : 0}%`,
                              backgroundColor: color,
                              minHeight: "3px",
                              cursor: "pointer",
                            }}
                            onClick={() => setTappedCountry(tappedCountry === country ? null : country)}
                          />
                        );
                      }) : (
                        <div
                          className="rounded-b-lg"
                          style={{
                            height: `${maxBarVal > 0 ? (numCountries / maxBarVal) * 100 : 0}%`,
                            backgroundColor: "#3b82f6",
                            minHeight: numCountries > 0 ? "3px" : "0",
                          }}
                        />
                      )}
                    </div>
                    {tappedCountry && (
                      <div
                        className="absolute -top-7 left-1/2 -translate-x-1/2 whitespace-nowrap text-white text-xs font-medium px-2.5 py-1 rounded-lg shadow-lg z-10"
                        style={{ backgroundColor: `hsl(${210 + countryList.indexOf(tappedCountry) * 30}, 70%, ${50 + countryList.indexOf(tappedCountry) * 8}%)` }}
                      >
                        {tappedCountry}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 mt-2">
                    <Globe className="w-3.5 h-3.5 text-muted-foreground" />
                    <span className="text-[11px] text-muted-foreground text-center">Countries</span>
                  </div>
                </div>

                <div className="flex flex-col items-center">
                  <span className="text-2xl font-bold mb-1">{numShared}</span>
                  <div className="w-12 h-28 rounded-lg bg-muted overflow-hidden flex flex-col-reverse">
                    <div
                      className="rounded-b-lg transition-all duration-700"
                      style={{
                        height: `${maxBarVal > 0 ? (numShared / maxBarVal) * 100 : 0}%`,
                        backgroundColor: "#8b5cf6",
                        minHeight: numShared > 0 ? "3px" : "0",
                      }}
                    />
                  </div>
                  <div className="flex items-center gap-1 mt-2">
                    <Share2 className="w-3.5 h-3.5 text-muted-foreground" />
                    <span className="text-[11px] text-muted-foreground text-center">Shared</span>
                  </div>
                </div>
              </div>

              {catEntries.length > 0 && (
                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-4 pt-3 border-t justify-center">
                  {stackSegments.map((seg) => (
                    <div key={seg.cat} className="flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: seg.hex }} />
                      <span className="text-[11px] text-muted-foreground">{seg.label} ({seg.count})</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </section>

        {/* My Collections / Folders */}
        <section data-testid="my-folders-section">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Folder className="w-4 h-4 text-primary" />
              <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">My Collections</h2>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs gap-1.5 rounded-lg"
              onClick={() => { setNewFolderName(""); setCreateFolderOpen(true); }}
              data-testid="create-folder-btn"
            >
              <FolderPlus className="w-3.5 h-3.5" />
              New Folder
            </Button>
          </div>

          {draggingPlaceId && (
            <div className="text-xs text-primary font-medium mb-2 animate-in fade-in flex items-center gap-1.5" data-testid="drag-hint">
              <GripVertical className="w-3 h-3" />
              Drop onto a folder to organize, or onto "My audio guides" to unfile
            </div>
          )}

          <div className="space-y-1">
            {sortedFolders.map((folder, idx) => (
              <FolderCard
                key={folder.id}
                folder={folder}
                index={idx}
                historyByPlace={historyByPlace}
                navigate={navigate}
                onRename={(f) => { setRenamingFolder(f); setRenameValue(f.name); }}
                onDelete={(f) => setDeletingFolder(f)}
                onGuideDrop={handleDrop}
                onFolderDrop={handleFolderReorder}
                draggingPlaceId={draggingPlaceId}
                draggingFolderId={draggingFolderId}
                setDraggingPlaceId={setDraggingPlaceId}
                setDraggingFolderId={setDraggingFolderId}
                clearDragging={clearDragging}
                isDefault={!!folder.is_default}
                sortMode={sortMode}
                sortAscending={sortAscending}
              />
            ))}

            {sortedFolders.length === 0 && !foldersLoading && (
              <div className="text-center py-4">
                <p className="text-sm text-muted-foreground">No folders yet. Create one to organize your guides.</p>
              </div>
            )}
          </div>
        </section>

        {/* My Audio Guides — root (unfiled) list, also a drop target */}
        <section
          data-testid="my-audio-guides-section"
          onDragOver={(e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
            setRootDragOver(true);
          }}
          onDragLeave={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) {
              setRootDragOver(false);
            }
          }}
          onDrop={handleRootDrop}
          className={`rounded-xl transition-all p-3 -m-3 ${
            rootDragOver ? "ring-2 ring-primary/20 bg-primary/5" : ""
          }`}
        >
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-2">
              <Headphones className="w-4 h-4 text-primary" />
              <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">My audio guides</h2>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 gap-1 px-2 text-xs"
              onClick={() => setSortAscending((current) => !current)}
              title={sortAscending ? "Sort descending" : "Sort ascending"}
              data-testid="library-sort-direction"
            >
              {sortAscending ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              {sortAscending ? "Ascending" : "Descending"}
            </Button>
          </div>
          <div className="flex flex-wrap gap-2 mb-3" role="group" aria-label="Sort audio guides" data-testid="library-sort-modes">
            {[
              ["newest", "Newest"],
              ["alpha", "A-Z"],
              ["rating", "Rating"],
            ].map(([mode, label]) => (
              <Button
                key={mode}
                type="button"
                size="sm"
                variant={sortMode === mode ? "default" : "outline"}
                className="h-8 rounded-full px-3 text-xs"
                onClick={() => selectSortMode(mode)}
                data-testid={`library-sort-${mode}`}
              >
                {label}
              </Button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            {draggingPlaceId ? "Drop here to remove from folder" : "Drag guides to folders above to organize them"}
          </p>

          {Object.values(historyByPlace).length === 0 ? (
            <Card>
              <CardContent className="py-10 text-center">
                <Headphones className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-muted-foreground text-sm">No audio guides yet</p>
                <Button variant="outline" className="mt-4 rounded-xl" onClick={() => navigate("/explore")} data-testid="explore-map-btn">
                  <MapPin className="w-4 h-4 mr-2" />
                  Explore the map
                </Button>
              </CardContent>
            </Card>
          ) : rootGuides.length === 0 ? (
            <div className="py-6 text-center border border-dashed border-border rounded-xl">
              <p className="text-sm text-muted-foreground">All guides are organized in folders</p>
            </div>
          ) : (
            <div className="space-y-2">
              {rootGuides.map((item) => (
                <GuideCard
                  key={item.place_id}
                  item={item}
                  onDragStart={setDraggingPlaceId}
                  onDragEnd={clearDragging}
                  navigate={navigate}
                  isDragging={draggingPlaceId === item.place_id}
                  dragData={{ placeId: item.place_id, fromFolderId: null }}
                />
              ))}
            </div>
          )}
        </section>
      </main>

      {/* Create Folder Dialog */}
      <Dialog open={createFolderOpen} onOpenChange={setCreateFolderOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Create Folder</DialogTitle>
            <DialogDescription>
              Give your folder a name, e.g. "London", "Italy 2026", "Museums"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <Input
              placeholder="Folder name"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCreateFolder()}
              maxLength={60}
              autoFocus
              data-testid="new-folder-name-input"
            />
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setCreateFolderOpen(false)}>
                Cancel
              </Button>
              <Button className="flex-1" onClick={handleCreateFolder} disabled={!newFolderName.trim()} data-testid="create-folder-confirm-btn">
                Create
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rename Folder Dialog */}
      <Dialog open={!!renamingFolder} onOpenChange={(open) => { if (!open) setRenamingFolder(null); }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Rename Folder</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <Input
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleRenameFolder()}
              maxLength={60}
              autoFocus
              data-testid="rename-folder-input"
            />
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setRenamingFolder(null)}>
                Cancel
              </Button>
              <Button className="flex-1" onClick={handleRenameFolder} disabled={!renameValue.trim()} data-testid="rename-folder-confirm-btn">
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Folder Confirmation */}
      <Dialog open={!!deletingFolder} onOpenChange={(open) => { if (!open) setDeletingFolder(null); }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Folder</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deletingFolder?.name}"? Your audio guides will not be deleted.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => setDeletingFolder(null)}>
              Cancel
            </Button>
            <Button variant="destructive" className="flex-1" onClick={handleDeleteFolder} data-testid="delete-folder-confirm-btn">
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <BottomNav active="/dashboard" />
    </div>
  );
};

export default DashboardPage;
